<template>
  <div>
    <iq-card>
      <template #headerTitle>
        <div class="d-flex align-items-center">
          <i
            v-if="!$route.query?.vehicleId"
            class="fa fa-arrow-left mr-3 text-primary"
            style="cursor: pointer; font-size: 20px"
            @click="$router.go(-1)"
          ></i>
          <h4 class="card-title text-primary mb-0">
            <strong>PMS FLEETS</strong>
          </h4>
        </div>
      </template>

      <template #body>
        <!-- Loading Overlay -->
        <div v-if="isLoading" class="text-center my-5">
          <b-spinner label="Loading..."></b-spinner>
          <p class="text-muted mt-2">Loading data, please wait...</p>
        </div>

        <div v-else>
          <!-- Header Top: Fleet Card + Running Hours Card -->
          <div class="row no-gutters px-2 mb-3">
            <!-- Fleet Card Left -->
            <div class="col-md-7 pr-md-3 mb-3" v-if="fleetDetail">
              <div class="card shadow-sm h-100 p-3">
                <div class="row no-gutters">
                  <div class="col-md-6">
                    <div class="fleet-image">
                      <img
                        :src="
                          fleetDetail.img?.url ??
                          fleetDetail.vehicle_type.fileurl
                        "
                        alt="Fleet Image"
                        class="w-100 h-100 object-fit-cover"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="pl-3">
                      <div class="d-flex align-items-center mb-2">
                        <img
                          :src="require('../Components/Icons/icon-fleet.svg')"
                          class="icon-badge mr-2"
                        />
                        <div>
                          <h5 class="mb-0 text-primary font-weight-bold">
                            {{ fleetDetail.name }}
                            <span class="ml-2 text-danger">●</span>
                          </h5>
                          <strong class="text-black mb-2">
                            {{ fleetDetail.company?.company }}
                          </strong>
                        </div>
                      </div>
                      <table class="table table-striped mb-0">
                        <tbody>
                          <tr>
                            <td class="text-muted">Fleet Type</td>
                            <td class="font-weight-bold text-capitalize">
                              {{ fleetDetail.vehicle_type?.name }}
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted">Call Sign</td>
                            <td class="font-weight-bold">
                              {{ fleetDetail.code }}
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted">Flag</td>
                            <td class="font-weight-bold">
                              {{ fleetDetail.flag }}
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted">Year & Building</td>
                            <td class="font-weight-bold">
                              {{ fleetDetail.year_build_country }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Running Hours Right -->
            <div class="col-md-5 pl-md-3 mb-3">
              <div class="card shadow-sm h-100">
                <div class="p-3 h-100 d-flex flex-column">
                  <div class="d-flex">
                    <div class="mr-2">
                      <img src="./Icons/clock-icon.svg" />
                    </div>
                    <div class="d-flex justify-content-between w-100 mb-2">
                      <div>
                        <h5 class="mb-0 text-primary font-weight-bold">
                          Total Running
                        </h5>
                        <div class="text-muted mb-3">
                          Last Update : {{ lastUpdate }}
                        </div>
                      </div>
                      <b-button
                        size="sm"
                        variant="warning"
                        :disabled="isLoading"
                        style="height: max-content"
                      >
                        Detail RH
                      </b-button>
                    </div>
                  </div>
                  <table class="table table-striped mb-0">
                    <tbody>
                      <tr v-for="(item, i) in runningHours" :key="i">
                        <td class="text-black" style="width: 60%">
                          <strong>
                            {{ item.name }}
                          </strong>
                          <br />
                          <small>
                            {{ item?.power_series?.name }} -
                            {{ item?.power_series?.model }}
                          </small>
                        </td>
                        <td class="text-right font-weight-bold text-black">
                          {{ formatHours(item.hours) }} Hours
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="mt-auto"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="px-2 mb-3">
            <PlanMaintenanceSummary :meta="summaryData" />
          </div>

          <!-- Controls -->
          <div class="d-flex align-items-center px-2 mb-3">
            <form @submit.prevent="fetchData">
              <b-form-input
                v-model="filters.search"
                style="max-width: 260px"
                placeholder="Search Equipment Name"
                :disabled="isLoading"
              />
            </form>
            <b-button
              variant="outline-primary"
              class="ml-2"
              @click="showFilter = true"
              :disabled="isLoading"
            >
              <i class="fas fa-sliders"></i>
              Filter Data
            </b-button>
            <b-button
              variant="primary"
              class="ml-auto"
              :disabled="isLoading"
              @click="handleExport"
            >
              <i class="fa fa-file-export mr-1"></i>
              Export
            </b-button>
          </div>

          <!-- Table -->
          <div class="table-responsive px-2">
            <table
              class="table align-middle table-striped table-bordered table-hover"
            >
              <thead class="thead-light">
                <tr>
                  <th rowspan="2" class="align-middle">Job Name</th>
                  <th rowspan="2" class="align-middle">Equipment</th>
                  <th rowspan="2" class="align-middle text-nowrap">Job Type</th>
                  <th rowspan="2" class="align-middle text-center">Interval</th>
                  <th rowspan="2" class="align-middle">Last</th>
                  <th rowspan="2" class="align-middle">Due</th>
                  <th rowspan="2" class="align-middle text-center">Status</th>
                  <th colspan="3" class="text-center">Summary Status</th>
                  <th rowspan="2" class="align-middle text-center">Action</th>
                </tr>
                <tr>
                  <th class="text-center">On Time</th>
                  <th class="text-center">Overdue</th>
                  <th class="text-center">Postponed</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  style="font-size: 12px"
                  v-for="(row, idx) in paginatedRows"
                  :key="idx"
                >
                  <!-- Column 1: Job Name -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 text-nowrap">
                      {{ row?.job_library?.name }}
                    </div>
                    <div class="border-top p-2" v-if="row?.method == 'COUNTER'">
                      <small class="text-muted text-nowrap">
                        {{ row?.maintenances?.power_type?.name ?? '-' }} |
                        {{ row?.maintenances?.power_series?.name }} -
                        {{ row?.maintenances?.power_series?.model }}
                      </small>
                    </div>
                  </td>

                  <!-- Column 2: Equipment -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 text-primary text-nowrap">
                      {{ row.equipment_name || '-' }}
                    </div>
                    <div class="border-top p-2">
                      <small class="text-muted text-nowrap">
                        {{ row.component_name || '-' }}
                      </small>
                    </div>
                  </td>

                  <!-- Column 3: Job Type -->
                  <td class="text-nowrap p-0">
                    <div class="font-weight-bold p-2">{{ row.job_type }}</div>
                    <div class="border-top p-2">
                      <small class="text-muted">{{ row.job_class }}</small>
                    </div>
                  </td>

                  <!-- Column 4: Interval -->
                  <td class="text-nowrap text-center p-0">
                    <div class="font-weight-bold p-2">{{ row.interval }}</div>
                    <div class="border-top p-2">
                      <small class="text-muted">{{ row.method }}</small>
                    </div>
                  </td>

                  <!-- Column 5: Last -->
                  <td key="" class="p-0">
                    <div class="text-nowrap p-2">
                      {{ row.last_maintenance ?? '-' }}
                    </div>
                    <div class="border-top text-nowrap p-2">
                      RH : {{ formatHours(row.last_maintenance_hours) }} Hr
                    </div>
                  </td>

                  <!-- Column 6: Due -->
                  <td v-if="row?.method == 'CALENDAR'">
                    {{
                      formatDate(row.maintenances?.possible_maintenance_date)
                    }}
                  </td>
                  <td v-else>
                    {{
                      formatNumber(
                        row.maintenances?.possible_maintenance_hours
                      ) || '0'
                    }}
                    Hr
                  </td>

                  <!-- Column 7: Status -->
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="getStatusBadgeClass(row.status)"
                    >
                      {{ row.status }}
                    </span>
                  </td>

                  <!-- Column 8: On Time -->
                  <td class="text-center">{{ row?.onTime ?? '-' }}</td>

                  <!-- Column 9: Overdue -->
                  <td class="text-center">{{ row.overdue }}</td>

                  <!-- Column 10: Postponed -->
                  <td class="text-center">{{ row.postponed }}</td>

                  <!-- Column 11: Action -->
                  <td class="text-center">
                    <b-dropdown
                      size="sm"
                      variant="outline-primary"
                      text="Action"
                      right
                    >
                      <b-dropdown-item @click.prevent="logAllHistory(row)">
                        Log All History
                      </b-dropdown-item>
                      <b-dropdown-item @click="deleteJob(row)">
                        Delete Job
                      </b-dropdown-item>
                    </b-dropdown>
                  </td>
                </tr>
                <tr v-if="!paginatedRows.length">
                  <td colspan="11" class="text-center text-muted py-4">
                    No data available
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer: Pagination -->
          <div
            class="mt-3 d-flex align-items-center w-100 justify-content-between px-3 pb-2"
            v-if="totalRows > 0"
          >
            <div>
              Total Rows :
              <strong>{{ totalRows }}</strong>
            </div>
            <div class="d-flex align-items-center">
              <b-form-select
                v-model="pagination.limit"
                :options="limitOptions"
                style="width: 140px"
                class="mr-2"
                @change="onChangeLimit"
              />
              <b-pagination
                class="mb-0"
                v-model="pagination.page"
                :total-rows="totalRows"
                :per-page="pagination.limit"
                first-number
                align="center"
              />
            </div>
          </div>
        </div>
      </template>
    </iq-card>

    <!-- Filter Modal -->
    <b-modal
      v-model="showFilter"
      centered
      size="lg"
      title="Filter Data"
      ok-title="Filter"
      @ok="fetchData"
    >
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="resetFilter">
          <i class="fas fa-undo mr-2"></i>
          Reset Filter
        </b-button>
        <b-button variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="primary" @click="ok()">Apply Filter</b-button>
      </template>
      <template>
        <FilterMaintenanceFormNew v-model="filters" />
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from '@/node_modules/moment/moment'
import PlanMaintenanceSummary from './PlanMaintenanceSummary.vue'
import FilterMaintenanceFormNew from './FilterMaintenanceFormNew.vue'
import { limitOptions } from '@/src/core/mock/data/optionsData'
import { pmsActions } from '@/src/Utils/helper'
import { getMiStatusBadgeClass } from '@/src/plugins/helpers'

export default {
  name: 'MaintenanceFleetDetail',
  components: { PlanMaintenanceSummary, FilterMaintenanceFormNew },
  data() {
    return {
      isLoading: false,
      limitOptions,
      showFilter: false,
      fleetDetail: null,
      lastUpdate: moment().format('DD MMMM YYYY'),
      summaryData: {
        active_maintenance: 0,
        toward_maintenance: 0,
        overdue_maintenance: 0,
        postpone_maintenance: 0,
      },
      runningHours: [
        { name: 'Main Engine Portside', hours: 1550 },
        { name: 'Main Engine Starboard', hours: 1550 },
        { name: 'Auxiliary Engine Portside', hours: 1230 },
        { name: 'Auxiliary Engine Starboard', hours: 1230 },
      ],
      filters: {
        search: '',
        dateRange: [],
        status: [],
        job_type: [],
        job_class: [],
        master_component_ids: [],
        component_ids: [],
      },
      tableRows: [],
      pagination: {
        page: 1,
        limit: 10,
      },
    }
  },
  computed: {
    filteredRows() {
      let rows = [...this.tableRows]
      if (this.filters.search && this.filters.search.trim() !== '') {
        const s = this.filters.search.toLowerCase().trim()
        rows = rows.filter(
          (r) =>
            r.equipment_name.toLowerCase().includes(s) ||
            r.component_name.toLowerCase().includes(s) ||
            r.jobType.toLowerCase().includes(s) ||
            r.jobClass.toLowerCase().includes(s)
        )
      }
      return rows
    },
    totalRows() {
      return this.filteredRows.length
    },
    paginatedRows() {
      const start = (this.pagination.page - 1) * this.pagination.limit
      return this.filteredRows.slice(start, start + this.pagination.limit)
    },
  },
  methods: {
    ...pmsActions,
    formatDate(date) {
      // Convert date using moment to DD/MM/YYYY format
      if (!date) return '-'

      return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },
    formatNumber(number) {
      // Format number with thousand separators
      if (!number && number !== 0) return '-'

      return Number(number).toLocaleString('id-ID')
    },
    async handleExport() {
      try {
        const payload = {
          ...this.filters,
          vehicle_id: this.$route.query.fleet_id,
        }
        const res = await this.exportPmsSummaryByVehicle(payload)
        saveAs(res.data, `maintenance_summary_${this.fleetDetail.name}.xlsx`)
        this.$swal({
          title: 'Success',
          text: res.message,
          icon: 'success',
        })
      } catch (error) {
        console.log(error)
        this.$swal({
          title: 'Failed Export Maintenance Summary',
          icon: 'error',
        })
      }
    },
    async fetchRunningHours() {
      try {
        const payload = {
          vehicle_id: this.$route.query.fleet_id || this.$route.query.vehicleId,
        }
        const res = await this.getPmsRunningHours(payload)
        if (res.status === 'success') {
          console.log(res.data)
          const mappingData = res.data.data.map((item) => ({
            name: item.power_type?.name,
            hours: item?.total_running_hour ?? '0',
            power_series: item.power_series,
          }))
          this.runningHours = mappingData
        }
      } catch (error) {
        console.error(error)
      }
    },
    buildPayload() {
      const mappingIds = (data) => {
        return data?.map((el) => el.code)?.toString()
      }
      const payload = {
        action: this.filters?.action,
        search: this.filters?.search,
        status: this.filters?.status,
        triggers: this.filters?.method,
        filterJobType: this.filters?.filterJobType,
        maintenance_component_id: mappingIds(
          this.filters?.master_component_ids
        ),
        maintenance_master_id: mappingIds(this.filters?.component_ids),
        job_type_id: mappingIds(this.filters?.job_type),
        job_class_id: mappingIds(this.filters?.job_class),
      }
      return payload
    },
    async fetchData() {
      this.isLoading = true
      try {
        const payload = {
          ...this.buildPayload(),
          vehicle_id: this.$route.query.fleet_id || this.$route.query.vehicleId,
        }
        const res = await this.getPmsSummaryByVehicle(payload)
        if (res.status === 'success') {
          this.fleetDetail = res.data.vehicle
          this.tableRows = res.data.data
          this.summaryData = res.data.counting_board
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    deleteJob(row) {
      this.$swal({
        title: 'Delete Data?',
        text: `Data will be deleted and cannot be recovered.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Delete',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const res = await this.deletejobPms({
            pms_vehicle_id: row.pms_vehicle_id,
          })
          if (res.status == 'success') {
            this.$swal(
              'Success',
              res?.data?.message ?? `Data deleted!`,
              'success'
            )
            this.fetchData()
          } else {
            this.$swal(
              'Error',
              res.data?.message ?? 'Something wrong with the server.',
              'error'
            )
          }
        }
      })
    },
    formatHours(val) {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('id-ID').format(val)
    },
    getStatusBadgeClass(status) {
      return getMiStatusBadgeClass(status)
    },
    onChangeLimit() {
      this.pagination.page = 1
    },
    resetFilter() {
      this.filters = {
        search: '',
        dateRange: [],
        status: [],
        job_type: [],
        job_class: [],
        master_component_ids: [],
        component_ids: [],
      }
      this.fetchData()
    },
    processRow(row) {},
    postponeRow(row) {},
    detailPms(row) {},
    editJobLibrary(row) {},
    logAllHistory(row) {
      this.$router.push({
        path: 'maintenance-history-by-job',
        query: { pms_vehicle_id: row.pms_vehicle_id },
      })
    },
    deleteRow(row) {},
  },
  mounted() {
    this.fetchData()
    this.fetchRunningHours()
  },
}
</script>

<style scoped>
.fleet-image {
  width: 100%;
  height: 240px;
  background: #e9edf4;
  border-radius: 0.25rem;
  overflow: hidden;
}
.icon-badge {
  width: 60px;
  height: 60px;
  background: #f4efe6;
  border-radius: 6px;
  display: inline-block;
}
thead.thead-light th {
  background: #f8f9fa;
}
tr td {
  line-height: 1.4;
}
</style>
